import React from "react";

export default function AICapabilityGrowthChart() {
  // Set dimensions and margins (same as original)
  const width = 1000;
  const height = 700;
  const margin = { top: 70, right: 140, bottom: 50, left: 80 };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  // Date labels from the provided data
  const dates = [
    "2025-04-30", "2025-08-31", "2025-12-31", "2026-04-30", "2026-08-31", "2026-12-31", 
    "2027-01-31", "2027-02-28", "2027-03-31", "2027-04-30", "2027-05-31", "2027-06-30", 
    "2027-07-31", "2027-08-31", "2027-09-30", "2027-10-15", "2027-10-31", "2027-11-30", "2027-12-31"
  ];

  // Format the dates for display
  const formattedDates = dates.map(date => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  });

  // Data arrays from the provided input
  const hackingData = [0.76, 0.95, 1.20, 1.33, 1.47, 1.63, 1.80, 1.94, 2.09, 2.24, 2.42, 2.60, 2.79, 2.99, 3.20, 3.43, 3.66, 4.19, 4.80];
  const codingData = [1.19, 1.42, 1.70, 1.79, 1.89, 1.99, 2.10, 2.26, 2.42, 2.60, 2.79, 3.00, 3.19, 3.39, 3.60, 3.81, 4.02, 4.48, 5.00];
  const politicsData = [0.38, 0.48, 0.60, 0.68, 0.77, 0.88, 1.00, 1.15, 1.32, 1.52, 1.74, 2.00, 2.29, 2.62, 3.00, 3.24, 3.48, 4.05, 4.70];
  const bioweaponsData = [0.73, 0.90, 1.10, 1.19, 1.28, 1.39, 1.50, 1.63, 1.78, 1.94, 2.11, 2.30, 2.48, 2.68, 2.90, 3.16, 3.43, 4.06, 4.80];
  const roboticsData = [0.06, 0.08, 0.10, 0.12, 0.14, 0.17, 0.20, 0.24, 0.29, 0.35, 0.42, 0.50, 0.77, 1.17, 1.80, 2.07, 2.35, 3.06, 4.00];

  // Thresholds and labels
  const thresholds = [0, 1, 2, 3, 4, 5];
  const thresholdLabels = ["", "amateur", "professional", "superhuman", "superhuman", ""];

  // Colors for the lines
  const colors = {
    hacking: "#000100",    // Black
    coding: "#508050",     // Medium green
    politics: "#004000",   // Dark green
    bioweapons: "#AA0000", // Red
    robotics: "#0000AA",   // Blue
    axis: "#444444",       // Dark gray for axis
    text: "#333333"        // Dark gray text
  };

  // Scales: x based on index, y based on value
  const xScale = (i: number) => margin.left + (i / (dates.length - 1)) * innerWidth;
  
  // Linear scale for the y-axis
  const yScale = (value: number) => {
    const maxValue = 5.5; // Max y-axis value slightly above 5
    return margin.top + innerHeight - (value / maxValue) * innerHeight;
  };
  
  // Generate x-values for all data points
  const xValues = dates.map((_, i) => xScale(i));

  // Choose select x-axis tick indices (showing fewer dates for clarity)
  const xTickIndices = [0, 3, 6, 9, 12, 15, 18];

  // Generate line paths for each dataset
  const generateLinePath = (dataArray: number[]) => {
    return dataArray.map((value, i) => 
      `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(value)}`
    ).join(' ');
  };

  // Generate paths for each capability
  const hackingPath = generateLinePath(hackingData);
  const codingPath = generateLinePath(codingData);
  const politicsPath = generateLinePath(politicsData);
  const bioweaponsPath = generateLinePath(bioweaponsData);
  const roboticsPath = generateLinePath(roboticsData);

  return (
    <div className="w-full relative overflow-hidden mt-8 mb-8">
      <svg
        width="100%"
        preserveAspectRatio="xMidYMid meet"
        viewBox={`0 0 ${width} ${height}`}
      >
        {/* Chart Title */}
        <text
          x={margin.left}
          y={40}
          fontSize="32"
          fontFamily="sans-monospace"
          className="text-[32px] font-mono md:text-[32px] sm:text-[32px]"
          fill={colors.text}
        >
          AI Capability Growth by Domain
        </text>

        {/* X-axis ticks and labels */}
        {xTickIndices.map((index) => {
          const x = xValues[index];
          return (
            <g key={`x-tick-${index}`}>
              <line 
                x1={x} 
                y1={height - margin.bottom} 
                x2={x} 
                y2={margin.top + 20} 
                stroke={colors.axis}
                strokeWidth="0.5" 
                strokeDasharray="5,5"
                opacity="0.15"
              />
              <text 
                x={x} 
                y={height - margin.bottom / 2} 
                textAnchor="middle" 
                className="text-[16px] font-mono md:text-[16px] sm:text-[16px]"
                fill={colors.text}
              >
                {formattedDates[index]}
              </text>
            </g>
          );
        })}

        {/* Y-axis Label */}
        <text
          transform={`rotate(-90)`}
          y={10}
          x={-height / 2}
          dy="1em"
          textAnchor="middle"
          className="text-[22px] font-mono md:text-[22px] sm:text-[22px]"
          fill={colors.text}
        >
          Capability Level
        </text>

        {/* Horizontal threshold lines */}
        {thresholds.map((value, i) => {
          const y = yScale(value);
          if (y < margin.top || y > height - margin.bottom) return null;
          
          return (
            <g key={`threshold-${i}`}>
              <line 
                x1={margin.left} 
                y1={y} 
                x2={width - margin.right} 
                y2={y} 
                stroke="#ddd" 
                strokeWidth="1" 
                strokeDasharray={value === 0 ? "0" : "5,5"}
              />
              
              <text 
                x={margin.left - 10} 
                y={y + 4} 
                textAnchor="end" 
                className="text-[18px] font-mono md:text-[18px] sm:text-[18px]"
                fill={colors.text}
              >
                {value}
              </text>
              
              {/* Threshold labels */}
              {thresholdLabels[i] && (
                <text 
                  x={width - margin.right + 10} 
                  y={y + 4} 
                  textAnchor="start" 
                  className="text-[16px] font-mono md:text-[16px] sm:text-[16px]"
                  fill={colors.text}
                >
                  {thresholdLabels[i]}
                </text>
              )}
            </g>
          );
        })}
        <text 
            x={width - margin.right + 10} 
            y={210} 
            textAnchor="start" 
            className="text-[16px] font-mono md:text-[16px] sm:text-[16px]"
            fill={colors.text}
            >
            wildly
        </text>
        {/* Lines for each capability */}
        <path 
          d={hackingPath} 
          fill="none" 
          stroke={colors.hacking} 
          strokeWidth="2.5" 
        />
        <path 
          d={codingPath} 
          fill="none" 
          stroke={colors.coding} 
          strokeWidth="2.5" 
        />
        <path 
          d={politicsPath} 
          fill="none" 
          stroke={colors.politics} 
          strokeWidth="2.5" 
        />
        <path 
          d={bioweaponsPath} 
          fill="none" 
          stroke={colors.bioweapons} 
          strokeWidth="2.5" 
        />
        <path 
          d={roboticsPath} 
          fill="none" 
          stroke={colors.robotics} 
          strokeWidth="2.5" 
        />

        {/* Data points for each capability */}
        {hackingData.map((value, i) => (
          <circle 
            key={`hacking-${i}`}
            cx={xScale(i)} 
            cy={yScale(value)} 
            r="4" 
            fill={colors.hacking}
          />
        ))}
        
        {codingData.map((value, i) => (
          <circle 
            key={`coding-${i}`}
            cx={xScale(i)} 
            cy={yScale(value)} 
            r="4" 
            fill={colors.coding}
          />
        ))}
        
        {politicsData.map((value, i) => (
          <circle 
            key={`politics-${i}`}
            cx={xScale(i)} 
            cy={yScale(value)} 
            r="4" 
            fill={colors.politics}
          />
        ))}
        
        {bioweaponsData.map((value, i) => (
          <circle 
            key={`bioweapons-${i}`}
            cx={xScale(i)} 
            cy={yScale(value)} 
            r="4" 
            fill={colors.bioweapons}
          />
        ))}
        
        {roboticsData.map((value, i) => (
          <circle 
            key={`robotics-${i}`}
            cx={xScale(i)} 
            cy={yScale(value)} 
            r="4" 
            fill={colors.robotics}
          />
        ))}

        {/* Legend */}
        <g transform={`translate(${margin.left+15}, 75)`}>
          {/* Hacking */}
          <line x1="0" y1="10" x2="20" y2="10" stroke={colors.hacking} strokeWidth="2.5" />
          <circle cx="10" cy="10" r="4" fill={colors.hacking} />
          <text x="30" y="15" fill={colors.text} className="text-[16px] font-mono">Hacking</text>
          
          {/* Coding */}
          <line x1="0" y1="40" x2="20" y2="40" stroke={colors.coding} strokeWidth="2.5" />
          <circle cx="10" cy="40" r="4" fill={colors.coding} />
          <text x="30" y="45" fill={colors.text} className="text-[16px] font-mono">Coding</text>
          
          {/* Politics */}
          <line x1="0" y1="70" x2="20" y2="70" stroke={colors.politics} strokeWidth="2.5" />
          <circle cx="10" cy="70" r="4" fill={colors.politics} />
          <text x="30" y="75" fill={colors.text} className="text-[16px] font-mono">Political Maneuvering</text>
          
          {/* Bioweapons */}
          <line x1="0" y1="100" x2="20" y2="100" stroke={colors.bioweapons} strokeWidth="2.5" />
          <circle cx="10" cy="100" r="4" fill={colors.bioweapons} />
          <text x="30" y="105" fill={colors.text} className="text-[16px] font-mono">Bioweapons</text>
          
          {/* Robotics */}
          <line x1="0" y1="130" x2="20" y2="130" stroke={colors.robotics} strokeWidth="2.5" />
          <circle cx="10" cy="130" r="4" fill={colors.robotics} />
          <text x="30" y="135" fill={colors.text} className="text-[16px] font-mono">Robotics</text>
        </g>
      </svg>
    </div>
  );
}